import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { DateRange } from './types';
import { generateCalendarDays } from './utils';
import { useClickOutside } from '../../hooks/useClickOutside';

interface DatePickerCalendarProps {
  value: DateRange;
  onSelect: (date: Date) => void;
}

const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({ value, onSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const calendarDays = generateCalendarDays(currentMonth, value);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => {
    setShowMonthPicker(false);
    setShowYearPicker(false);
  });

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleMonthSelect = (month: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), month));
    setShowMonthPicker(false);
  };

  const handleYearSelect = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth()));
    setShowYearPicker(false);
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Generate years from 1900 to current year + 10
    for (let year = 1900; year <= currentYear + 10; year++) {
      years.push(year);
    }
    return years;
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className="p-3" ref={containerRef}>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2">
          {/* Month Picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowMonthPicker(!showMonthPicker);
                setShowYearPicker(false);
              }}
              className="flex items-center gap-1 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-sm font-medium"
            >
              {monthNames[currentMonth.getMonth()]}
              <ChevronDown className="w-3 h-3" />
            </button>

            {showMonthPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[120px] max-h-48 overflow-y-auto">
                {monthNames.map((month, index) => (
                  <button
                    key={month}
                    type="button"
                    onClick={() => handleMonthSelect(index)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      index === currentMonth.getMonth() ? 'bg-primary text-white' : ''
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Year Picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowYearPicker(!showYearPicker);
                setShowMonthPicker(false);
              }}
              className="flex items-center gap-1 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-sm font-medium"
            >
              {currentMonth.getFullYear()}
              <ChevronDown className="w-3 h-3" />
            </button>

            {showYearPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[80px] max-h-48 overflow-y-auto">
                {generateYearOptions().map(year => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => handleYearSelect(year)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      year === currentMonth.getFullYear() ? 'bg-primary text-white' : ''
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleNextMonth}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weekDays.map(day => (
          <div
            key={day}
            className="h-8 flex items-center justify-center text-sm text-gray-500 font-medium"
          >
            {day}
          </div>
        ))}

        {calendarDays.map((day, index) => (
          <button
            key={index}
            type="button"
            onClick={() => day.isCurrentMonth && !day.isDisabled && onSelect(day.date)}
            disabled={!day.isCurrentMonth || day.isDisabled}
            title={
              day.isDisabled
                ? 'Future dates are not allowed'
                : day.isCurrentMonth
                  ? 'Click to select date'
                  : ''
            }
            className={`
              h-8 w-8 flex items-center justify-center text-sm relative
              ${!day.isCurrentMonth ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : ''}
              ${day.isDisabled ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50' : ''}
              ${day.isCurrentMonth && !day.isDisabled ? 'cursor-pointer' : ''}
              ${day.isToday ? 'font-bold' : ''}
              ${day.isSelected ? 'bg-primary text-white rounded-full z-10' : ''}
              ${day.isInRange ? 'bg-primary/10 text-primary' : ''}
              ${!day.isSelected && !day.isInRange && day.isCurrentMonth && !day.isDisabled ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
            `}
          >
            {day.date.getDate()}
            {day.isInRange && !day.isRangeStart && !day.isRangeEnd && (
              <div className="absolute inset-y-0 -left-0.5 -right-0.5 bg-primary/10 -z-10" />
            )}
          </button>
        ))}
      </div>

      {/* Help text for date restrictions */}
      <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Future dates are not allowed
        </p>
      </div>
    </div>
  );
};

export default DatePickerCalendar;
